import React, { useMemo } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";

function Table({
  columns,
  data,
  filterInput,
  setFilterInput,
  pageCount,
  loading,
  handleEdit,
  handleDelete,
  onPageChange,
  currentPage,
  actionButtonLabel = "Add",
  showActionButton = false,
  onTrigger = () => {},
  onImportTrigger = () => {},
  className = "", // Accept className as a prop with a default empty string
}) {
  const filteredData = useMemo(() => {
    if (!filterInput) return data; // No filtering if there's no input
    return data.filter((row) =>
      Object.values(row).some(
        (value) =>
          String(value).toLowerCase().includes(filterInput.toLowerCase())
      )
    );
  }, [data, filterInput]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 10 },
      pageCount,
      manualPagination: true,
      sortBy: [{ id: 'name', desc: true }],
    },
    useSortBy,
    usePagination
  );

  // Call the onPageChange when the page changes
  const handleGotoPage = (pageIndex) => {
    gotoPage(pageIndex);
    onPageChange(pageIndex); // Trigger the page change in the parent
  };

  return (
    <div className="applications">
      <div className="d-flex justify-content-end mb-3">
        {showActionButton && actionButtonLabel === 'Add Community' && (
          <button
            type="button"
            onClick={onImportTrigger}
            className="btn btn-add-comm"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Import Communities
          </button>
        )}
        {showActionButton && (
          <button
            type="button"
            onClick={onTrigger}
            className="btn btn-add-comm mx-4"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {actionButtonLabel}
          </button>
        )}
        {data.length > 0 && (
          <div
            className="input-group"
            style={{ width: "300px", position: "relative" }}
          >
            <input
              type="text"
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              className="form-control ps-5"
              placeholder="Search..."
              style={{ backgroundColor: "transparent", borderColor: "#ccc" }}
            />
            <FontAwesomeIcon
              icon={faSearch}
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#ccc",
              }}
            />
          </div>
        )}
      </div>

      <div className="outer">
        <table
          {...getTableProps()}
          className={`table table-striped table-bordered ${className}`}
        >
          <thead>
  {headerGroups.map((headerGroup, headerIndex) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
    const { key: headerGroupKey, ...restHeaderGroupProps } = headerGroupProps; // Extract `key`

              return (
                <tr key={headerGroupKey} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const columnProps = column.getHeaderProps(column.getSortByToggleProps());
          const { key: columnKey, ...restColumnProps } = columnProps; // Extract `key`

                    return (
                      <th key={columnKey} {...restColumnProps}>
                        {column.render("Header")}
                        {!column.disableSortBy && (
                          <span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <FontAwesomeIcon icon={faSortDown} />
                              ) : (
                                <FontAwesomeIcon icon={faSortUp} />
                              )
                            ) : (
                              <FontAwesomeIcon icon={faSort} />
                            )}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>


          <tbody {...getTableBodyProps()}>
            {loading ? (
              <tr>
                <td colSpan={columns.length}>Loading...</td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No Data Available
                </td>
              </tr>
            ) : (
    page.map((row, rowIndex) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
      const { key: rowKey, ...restRowProps } = rowProps; // Extract `key`

                return (
                  <tr key={rowKey} {...restRowProps}>
          {row.cells.map((cell, cellIndex) => {
                      const cellProps = cell.getCellProps();
            const { key: cellKey, ...restCellProps } = cellProps; // Extract `key`

                      return (
                        <td key={cellKey} {...restCellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {filteredData.length > 0 && (
         
          <div className="pagination d-flex justify-content-end">
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="btn btn-secondary"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span>
              {Array.from({ length: pageCount }, (_, index) => (
                <button
                  key={index}
                  onClick={() => handleGotoPage(index)}
                  className={`btn btn-sm mx-1 ${currentPage === index ? "btn-primary active" : "btn-light"}`}
                  aria-current={currentPage === index ? "page" : undefined}
                >
                  {index + 1}
                </button>
              ))}
            </span>

            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="btn btn-secondary"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Table;
